import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import axios from "axios";

import { API } from "@/apiConfig";
const fadeUp = (delay = 0) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.55, delay, ease: "easeOut" } });

const DEFAULT_PAGE = {
  page_kicker: "Blog & Ressources",
  page_title: "Articles & Conseils",
  intro_text: "Des ressources pratiques pour intégrer la sophrologie dans votre quotidien et mieux comprendre cette discipline.",
  header_image: "",
  header_image_alt: "Articles et conseils sophrologie",
  empty_text: "Aucun article disponible pour le moment.",
  read_more_label: "Lire la suite",
};

function formatDate(dateStr) {
  if (!dateStr) return "";
  try { return new Date(dateStr).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
}

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Articles & Conseils — Eudemonie Sophrologie";
    axios.get(`${API}/public/pages/blog`).then(r => { if (r.data?.content) setPage(prev => ({ ...prev, ...r.data.content })); }).catch(() => {});
    axios.get(`${API}/public/articles`).then(r => setArticles(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-20">
      <section className="section-gap bg-bone">
        <div className="section-container">
          <motion.p {...fadeUp()} className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">{page.page_kicker}</motion.p>
          <motion.h1 {...fadeUp(0.1)} className="font-playfair text-5xl md:text-6xl text-charcoal mb-4">{page.page_title}</motion.h1>
          <motion.p {...fadeUp(0.2)} className="font-manrope text-charcoal-muted text-lg max-w-xl">
            {page.intro_text}
          </motion.p>
          {page.header_image && <motion.img {...fadeUp(0.25)} src={page.header_image} alt={page.header_image_alt} className="mt-10 rounded-[2rem] w-full max-h-[360px] object-cover shadow-lg" />}
        </div>
      </section>

      <section className="section-gap bg-bone-light">
        <div className="section-container">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2].map(i => <div key={i} className="rounded-2xl bg-bone h-72 animate-pulse" />)}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-manrope text-charcoal-muted">{page.empty_text}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a, i) => (
                <motion.div key={a.id || i} {...fadeUp(i * 0.1)} data-testid={`article-card-${i}`}>
                  <Link to={`/blog/${a.slug}`} className="card-soft group block h-full">
                    {a.image && (
                      <div className="rounded-xl overflow-hidden mb-5 h-48">
                        <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar size={12} className="text-sage" />
                      <span className="font-manrope text-xs text-charcoal-muted">{formatDate(a.published_at)}</span>
                      <span className="font-manrope text-xs text-charcoal-soft">• {a.author}</span>
                    </div>
                    <h2 className="font-playfair text-xl text-charcoal mb-3 group-hover:text-sage transition-colors duration-200">{a.title}</h2>
                    <p className="font-manrope text-sm text-charcoal-muted leading-relaxed mb-4">{a.excerpt}</p>
                    <div className="flex items-center gap-1 text-sage text-xs font-medium font-manrope group-hover:gap-2 transition-all duration-200">
                      {page.read_more_label} <ArrowRight size={12} />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
